import React, { useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// TODO: fix this, currently issues with ssr + module types + highcharts

// import Chart from 'highcharts/es-modules/Core/Chart/Chart.js';´
// import BarSeries from 'highcharts/es-modules/Series/Bar/BarSeries.js';
// import DataLabel from 'highcharts/es-modules/Core/Series/DataLabel.js';
// DataLabel.compose(BarSeries);

import Highcharts from 'highcharts';
import highchartsAccessibility from 'highcharts/modules/accessibility';

import ChartIntroLabel from '../ChartIntroLabel/ChartIntroLabel';

import { THEME_BLUE_PURPLE, THEME_ORANGE_RED } from './constants';

import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import useDelayedEffect from '../../hooks/useDelayedEffect';

import { AnimationsActiveContext } from '../../containers/BasePage';

import chartThemes from './chartThemes';
import s from './BarChartHorizontal.module.scss';

const formatPlotDataForHighcharts = (plotData, theme, textColor) => {
  const categories = plotData?.items?.map((item) => item.label);
  const seriesData = plotData?.items?.map((item) => ({ y: item.value }));

  const yAxisMin = plotData?.yAxisMin || 0;
  const yAxisMax = plotData?.yAxisMax
    ? plotData?.yAxisMax
    : seriesData?.reduce((max, v) => {
        max = v.y > max ? v.y : max;
        return max;
      }, 0) * 1.1; // get max y and increase it by 10% to add some visual margin in chart

  return {
    xAxis: {
      ...theme.xAxisLabelStyles,
      labels: {
        ...theme.xAxisLabelStyles.labels,
        style: {
          ...theme.xAxisLabelStyles.labels.style,
          color: textColor,
        },
      },
      categories,
    },
    yAxis: {
      min: yAxisMin,
      max: yAxisMax,
      ...theme.yAxisLabelStyles,
    },
    series: [
      {
        dataLabels: {
          ...theme.dataLabels,
          format:
            plotData?.dataLabelFormat?.replace('{value}', '{y}') ||
            theme.dataLabels.format,
          style: {
            ...theme.dataLabels.style,
            color: textColor,
          },
        },
        data: seriesData,
        states: {
          // manually done in css instead to allow for transition animation
          hover: {
            enabled: false,
          },
        },
      },
    ],
  };
};

const BarChartHorizontal = (props) => {
  const {
    title,
    description,
    infoTitle,
    infoText,
    infoLink,
    colorTheme,
    textTheme,
    chartHeight,
    plotData,
  } = props;

  const { animationsActive } = useContext(AnimationsActiveContext) || {};

  const chartContainerRef = useRef(null);
  const [inViewRef, isVisible] = useIntersectionObserver(0.2, undefined, true);

  const theme = chartThemes[colorTheme];
  const textColor = textTheme === 'light' ? '#ffffff' : '#000000';

  const formattedPlotData = formatPlotDataForHighcharts(
    plotData,
    theme,
    textColor
  );

  const chart = useRef(null);

  useDelayedEffect(
    animationsActive ? 900 : 10,
    () => {
      if (!chartContainerRef.current || !isVisible) {
        return;
      }

      highchartsAccessibility(Highcharts);

      chart.current = new Highcharts.Chart(chartContainerRef.current, {
        accessibility: {
          description: `${title}\n${description}`,
        },
        ...theme.theme,
        plotOptions: {
          ...theme.theme.plotOptions,
          series: {
            ...theme.theme.plotOptions.series,
            animation: animationsActive,
          },
        },
        chart: {
          type: 'bar',
          ...theme.theme.chart,
        },
        ...formattedPlotData,
      });
    },
    [isVisible]
  );

  const rootCls = classNames(s.Root, 'grid', {
    [s.Visible]: isVisible,
  });

  return (
    <div ref={inViewRef} className={rootCls}>
      <div className={s.Container}>
        {title && (
          <div className={s.Description}>
            <ChartIntroLabel
              theme={textTheme}
              title={title}
              description={description}
              infoTitle={infoTitle}
              infoText={infoText}
              infoLink={infoLink}
            />
          </div>
        )}
        <div
          ref={chartContainerRef}
          className={s.ChartContainer}
          style={{
            minHeight: chartHeight,
          }}></div>
      </div>
    </div>
  );
};

BarChartHorizontal.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  infoTitle: PropTypes.string,
  infoText: PropTypes.string,
  infoLink: PropTypes.object,
  colorTheme: PropTypes.oneOf([THEME_ORANGE_RED, THEME_BLUE_PURPLE]),
  textTheme: PropTypes.oneOf(['light', 'dark']),
  chartHeight: PropTypes.string,
  plotData: PropTypes.shape({
    dataLabelFormat: PropTypes.string,
    yAxisMin: PropTypes.number,
    yAxisMax: PropTypes.number,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.number.isRequired,
        label: PropTypes.string.isRequired,
      }).isRequired
    ),
  }).isRequired,
};

BarChartHorizontal.defaultProps = {
  colorTheme: THEME_ORANGE_RED,
  textTheme: 'light',
  chartHeight: '80vh',
  plotData: {},
};

export default BarChartHorizontal;
